import { ExternalLinkIcon } from "@chakra-ui/icons";
import { Avatar, Box, Flex, HStack, Link, Stack, Text, Tooltip } from "@chakra-ui/react";
import { shortenAddress, useEthers } from "@usedapp/core";
import BigNumber from "bignumber.js";
import { format } from "date-fns";
import { utils } from "ethers";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { I18N_NAMESPACE } from "../../../core/constants/i18n";
import { INftActivity } from "../../../core/models/nft";
import { getExplorerTxUrl } from "../../../core/utils/getExplorerUrl";
import { formatNumber } from "../../../core/utils/number";
import { useAppSelector } from "../../../store/hook";

interface Props {
  activities: INftActivity[];
}

export const ActivitiesInfo = React.memo((props: Props) => {
  const supportedTokens = useAppSelector((state) => state.token.supportedTokens);
  const navigate = useNavigate();
  const { chainId } = useEthers();
  const { t } = useTranslation([I18N_NAMESPACE.ASSET_DETAIL]);

  const onPressAddress = useCallback(
    (address: string) => () => {
      navigate(`/profile/assets/${address}`);
    },
    []
  );

  return (
    <Stack spacing={5}>
      {props.activities.length > 0 ? (
        props.activities.map((activity, index) => {
          if (activity.type === "transfer") {
            return (
              <HStack key={`activity-${index}`} spacing={3}>
                <Avatar src={activity?.from_account?.image || ""} size={"sm"} />
                <Box fontSize={"sm"} fontWeight={500} color="gray.500">
                  <Text fontWeight={500}>
                    {t("Tranferred to")}{" "}
                    <Text as="b" color="black">
                      <Link onClick={onPressAddress(activity.to)}>
                        {activity.to_account?.display_name || shortenAddress(activity.to)}
                      </Link>
                    </Text>
                  </Text>
                  <Flex alignItems={"flex-end"}>
                    <Text fontSize={"xs"} fontWeight={300}>
                      {t("by")}{" "}
                      <Text as="span" color="black" fontWeight={400}>
                        <Link onClick={onPressAddress(activity.from)}>{shortenAddress(activity.from)}</Link>
                      </Text>{" "}
                      {format(activity.timestamp, "dd/MM/yyyy HH:mm")}
                    </Text>
                    {!!chainId && (
                      <Link
                        target={"_blank"}
                        href={getExplorerTxUrl(activity.transaction_hash, chainId)}
                        marginLeft={2}
                      >
                        <Tooltip hasArrow label="View on explorer" borderRadius="lg" padding="6px" placement="top">
                          <ExternalLinkIcon fontSize={16} _hover={{ color: "#000" }} />
                        </Tooltip>
                      </Link>
                    )}
                  </Flex>
                </Box>
              </HStack>
            );
          }

          if (activity.type === "mint") {
            return (
              <HStack key={`activity-${index}`} spacing={3}>
                <Avatar src={activity?.to_account?.image || ""} size={"sm"} />
                <Box fontSize={"sm"} fontWeight={500} color="gray.500">
                  <Text fontWeight={500}>
                    {t("Minted by")}{" "}
                    <Text as="b" color="black">
                      <Link onClick={onPressAddress(activity.to)}>
                        {activity.to_account?.display_name || shortenAddress(activity.to)}
                      </Link>
                    </Text>
                  </Text>
                  <Flex alignItems={"flex-end"}>
                    <Text fontSize={"xs"} fontWeight={300}>
                      {format(activity.timestamp, "dd/MM/yyyy HH:mm")}{" "}
                    </Text>
                    {!!chainId && (
                      <Link
                        target={"_blank"}
                        href={getExplorerTxUrl(activity.transaction_hash, chainId)}
                        marginLeft={2}
                      >
                        <Tooltip hasArrow label={t("View on explorer")} borderRadius="lg" padding="6px" placement="top">
                          <ExternalLinkIcon fontSize={16} _hover={{ color: "#000" }} />
                        </Tooltip>
                      </Link>
                    )}
                  </Flex>
                </Box>
              </HStack>
            );
          }

          if (activity.type === "sold") {
            const token = activity.b_token ? supportedTokens.byId[activity.b_token] : null;
            return (
              <HStack key={`activity-${index}`} spacing={3}>
                <Avatar src={activity?.to_account?.image || ""} size={"sm"} />
                <Box fontSize={"sm"} fontWeight={500} color="gray.500">
                  <Text fontWeight={500}>
                    {t("Purchased for")}{" "}
                    <Text as="b" color="black">
                      {token
                        ? formatNumber(new BigNumber(activity?.price || 0).div(Math.pow(10, token.decimals)).toNumber())
                        : 0}{" "}
                      {token?.symbol}
                    </Text>
                  </Text>
                  <Flex alignItems={"flex-end"}>
                    <Text fontSize={"xs"} fontWeight={300}>
                      {" "}
                      {t("by")}{" "}
                      <Text as="span" color="black" fontWeight={400}>
                        <Link onClick={onPressAddress(activity.to)}>
                          {activity.to_account?.display_name || shortenAddress(activity.to)}
                        </Link>
                      </Text>{" "}
                      {activity.timestamp ? format(activity.timestamp, "dd/MM/yyyy HH:mm") : ""}{" "}
                    </Text>
                    {!!chainId && (
                      <Link
                        target={"_blank"}
                        href={getExplorerTxUrl(activity.transaction_hash, chainId)}
                        marginLeft={2}
                      >
                        <Tooltip hasArrow label={t("View on explorer")} borderRadius="lg" padding="6px" placement="top">
                          <ExternalLinkIcon fontSize={16} _hover={{ color: "#000" }} />
                        </Tooltip>
                      </Link>
                    )}
                  </Flex>
                </Box>
              </HStack>
            );
          }
        })
      ) : (
        <Box>
          <Text color={"gray.500"}>{t("No item activity yet")}</Text>
        </Box>
      )}
    </Stack>
  );
});
